import {CONTACT_EMAIL, LegalDocument, OPERATOR_NAME, OPERATOR_POSTAL_ADDRESS} from './types';

/**
 * Политика обработки персональных данных — документ по 152-ФЗ.
 *
 * В отличие от Политики конфиденциальности, которая объясняет происходящее
 * человеческим языком, здесь фиксируются формальные основания, цели, сроки и
 * права субъекта. Текст требует юридической проверки (requirements.md §7).
 */
export const PERSONAL_DATA_POLICY: LegalDocument = {
    type: 'PERSONAL_DATA_POLICY',
    title: 'Политика обработки персональных данных',
    shortTitle: 'Персональные данные',
    version: '1.0',
    effectiveOn: '2026-08-26',
    slug: 'personal-data',
    intro:
        'Политика определяет порядок обработки персональных данных в сервисе Clothes ' +
        'и составлена в соответствии с Федеральным законом от 27.07.2006 № 152-ФЗ ' +
        '«О персональных данных».',
    sections: [
        {
            id: 'pdn-operator',
            title: '1. Оператор',
            blocks: [
                {
                    kind: 'p',
                    text:
                        `Оператором персональных данных является ${OPERATOR_NAME}. ` +
                        `Адрес для направления письменных обращений: ${OPERATOR_POSTAL_ADDRESS}. ` +
                        `Адрес электронной почты: ${CONTACT_EMAIL}.`,
                },
                {
                    kind: 'p',
                    text:
                        'Политика распространяется на все персональные данные, которые Оператор получает ' +
                        'от субъектов персональных данных при использовании сервиса.',
                },
            ],
        },
        {
            id: 'pdn-terms',
            title: '2. Основные понятия',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Персональные данные — любая информация, относящаяся к прямо или косвенно определённому физическому лицу (субъекту персональных данных).',
                        'Обработка персональных данных — любое действие с данными: сбор, запись, систематизация, хранение, уточнение, использование, передача, обезличивание, блокирование, удаление, уничтожение.',
                        'Субъект персональных данных — пользователь сервиса, к которому относятся данные.',
                        'Трансграничная передача — передача персональных данных на территорию иностранного государства.',
                    ],
                },
            ],
        },
        {
            id: 'pdn-categories',
            title: '3. Категории обрабатываемых данных',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Идентификационные данные: адрес электронной почты, отображаемое имя.',
                        'Данные аутентификации: хеш пароля, признак подтверждения адреса, факторы двухфакторной аутентификации.',
                        'Пользовательский контент: фотографии предметов гардероба, их характеристики, заметки, оценки.',
                        'Данные об использовании сервиса: история носки и ухода, действия в разделах сервиса, история AI-запросов.',
                        'Технические данные: IP-адрес, сведения о браузере и устройстве, журналы обращений к сервису.',
                        'Данные о согласиях: состав, версия документа, дата, IP-адрес и клиент, с которых дано согласие.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Специальные категории персональных данных и биометрические персональные данные ' +
                        'Оператором целенаправленно не обрабатываются.',
                },
            ],
        },
        {
            id: 'pdn-purposes',
            title: '4. Цели и правовые основания',
            blocks: [
                {
                    kind: 'table',
                    head: ['Цель', 'Основание'],
                    rows: [
                        ['Предоставление доступа к сервису и его функциям', 'исполнение договора (п. 5 ч. 1 ст. 6 152-ФЗ)'],
                        ['Регистрация, аутентификация и восстановление доступа', 'исполнение договора'],
                        ['Хранение и отображение пользовательского контента', 'исполнение договора'],
                        ['Формирование рекомендаций и аналитики гардероба с использованием AI', 'согласие субъекта, отзывается в настройках приватности'],
                        ['Обработка фотографий для распознавания вещей', 'отдельное согласие субъекта'],
                        ['Направление информационных и рекламных сообщений', 'отдельное согласие субъекта, отзывается в настройках'],
                        ['Обеспечение безопасности, расследование инцидентов, ведение журнала аудита', 'законный интерес Оператора и требования законодательства'],
                        ['Фиксация факта согласия с документами сервиса', 'исполнение обязанности доказывания согласия (ч. 1 ст. 9 152-ФЗ)'],
                    ],
                },
            ],
        },
        {
            id: 'pdn-consent',
            title: '5. Порядок получения согласия',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Согласие с Пользовательским соглашением, Политикой конфиденциальности и настоящей ' +
                        'Политикой даётся при регистрации путём проставления соответствующей отметки. ' +
                        'Регистрация без такой отметки невозможна.',
                },
                {
                    kind: 'p',
                    text:
                        'Факт согласия фиксируется в журнале: тип документа, его версия, дата и время, ' +
                        'IP-адрес и сведения о клиенте. Записи журнала не изменяются и не удаляются ' +
                        'в течение всего срока их хранения.',
                },
                {
                    kind: 'p',
                    text:
                        'Согласия на AI-профилирование, обработку фотографий и получение рассылок даются ' +
                        'отдельно от обязательных документов и отзываются независимо друг от друга.',
                },
            ],
        },
        {
            id: 'pdn-processing',
            title: '6. Порядок и условия обработки',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Обработка осуществляется с использованием средств автоматизации. Базы данных ' +
                        'персональных данных граждан Российской Федерации размещаются на территории ' +
                        'Российской Федерации.',
                },
                {
                    kind: 'p',
                    text:
                        'Оператор принимает правовые, организационные и технические меры защиты: ' +
                        'разграничение доступа по ролям и правам, шифрование чувствительных полей, ' +
                        'передачу данных по защищённым каналам, ведение журнала аудита доступа, ' +
                        'резервное копирование.',
                },
            ],
        },
        {
            id: 'pdn-transfer',
            title: '7. Передача третьим лицам и трансграничная передача',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Оператор поручает обработку персональных данных следующим категориям лиц ' +
                        'в объёме, необходимом для соответствующей функции:',
                },
                {
                    kind: 'table',
                    head: ['Категория получателя', 'Что передаётся', 'Цель'],
                    rows: [
                        ['Поставщики моделей машинного обучения', 'содержимое конкретного AI-запроса: изображение вещи или её описание, характеристики гардероба, текст обращения к стилисту', 'выполнение AI-функции'],
                        ['Поставщики инфраструктуры (хостинг, объектное хранилище)', 'данные учётной записи и контент в зашифрованном канале', 'размещение и хранение'],
                        ['Поставщики почтовых сервисов', 'адрес электронной почты и текст письма', 'доставка писем подтверждения и уведомлений'],
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'Идентификационные данные (адрес почты, имя, идентификаторы учётной записи) ' +
                        'в AI-запросы не включаются.',
                },
                {
                    kind: 'p',
                    text:
                        'Часть поставщиков моделей машинного обучения находится за пределами Российской ' +
                        'Федерации, что означает трансграничную передачу. Такая передача осуществляется ' +
                        'на основании согласия субъекта, данного при включении AI-функций, и может быть ' +
                        'прекращена отзывом этого согласия в настройках приватности. Актуальный перечень ' +
                        `поставщиков предоставляется по запросу на ${CONTACT_EMAIL}.`,
                },
            ],
        },
        {
            id: 'pdn-retention',
            title: '8. Сроки хранения',
            blocks: [
                {
                    kind: 'table',
                    head: ['Категория', 'Срок'],
                    rows: [
                        ['Данные учётной записи и пользовательский контент', 'до удаления учётной записи субъектом'],
                        ['Данные удалённой учётной записи', 'уничтожаются в течение 30 дней с момента удаления, за исключением случаев ниже'],
                        ['Журнал согласий', 'в течение срока действия договора и 3 лет после его прекращения — как доказательство согласия'],
                        ['Журналы аудита и безопасности', 'до 1 года'],
                        ['Данные, хранение которых обязательно по закону', 'в течение установленного законом срока'],
                    ],
                },
                {
                    kind: 'p',
                    text:
                        'По достижении целей обработки или при отзыве согласия данные уничтожаются либо ' +
                        'обезличиваются, если иное не предусмотрено законом.',
                },
            ],
        },
        {
            id: 'pdn-rights',
            title: '9. Права субъекта персональных данных',
            blocks: [
                {kind: 'p', text: 'Субъект персональных данных вправе:'},
                {
                    kind: 'list',
                    items: [
                        'получать сведения об обработке своих данных: правовые основания, цели, способы, сроки, круг лиц, которым данные передаются;',
                        'требовать уточнения данных, их блокирования или уничтожения, если они неполны, устарели, неточны или получены незаконно;',
                        'отозвать согласие на обработку;',
                        'получить свои данные в машиночитаемом формате;',
                        'обжаловать действия Оператора в уполномоченном органе по защите прав субъектов персональных данных или в судебном порядке.',
                    ],
                },
                {
                    kind: 'p',
                    text:
                        `Запросы направляются на ${CONTACT_EMAIL} либо письменно по адресу ` +
                        `${OPERATOR_POSTAL_ADDRESS}. Ответ направляется в срок, установленный ` +
                        'законодательством, на адрес, с которого поступило обращение.',
                },
                {
                    kind: 'p',
                    text:
                        'Часть прав реализуется без обращения — самостоятельно в интерфейсе сервиса: ' +
                        'изменение данных профиля, отзыв отдельных согласий, выгрузка гардероба, ' +
                        'удаление учётной записи.',
                },
            ],
        },
        {
            id: 'pdn-changes',
            title: '10. Изменение политики',
            blocks: [
                {
                    kind: 'p',
                    text:
                        'Оператор вправе вносить изменения в настоящую Политику. Каждая редакция имеет ' +
                        'номер версии и дату вступления в силу; действующая редакция публикуется в сервисе. ' +
                        'О существенных изменениях субъекты уведомляются до вступления изменений в силу.',
                },
            ],
        },
    ],
};
